import { useFeatureGate } from '@statsig/react-bindings';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LOCALE_DEFAULT, useI18nReact } from '@growtherapy/i18n';

import { logger } from '../datadog/logger';
import {
  getPreferredSupportedLocale,
  hasStoredLanguagePreference,
  setStoredLanguagePreference,
} from '../i18n/languagePreference';
import { ADDITIONAL_SUPPORTED_LOCALES } from '../i18n/locales';
import { useTrackEvent } from '../segment/segment';
import { EventTypeEnum } from '../segment/types';
import { localizationTelehealthClientPromptGate } from '../statsig/gates';
import { normalizeError } from '../utils';
import { BrowserLanguagePromptModal } from './BrowserLanguagePrompt';
import { LOCALE_DETECTION_PROMPT_CONFIG } from './browserLanguagePromptConfig';

function BrowserLanguagePrompt() {
  const i18n = useI18nReact();
  const { userClicked, userViewed } = useTrackEvent();
  const hasTrackedViewRef = useRef(false);

  const [detectedLocale] = useState(() => {
    // If a language preference is already stored, don't show the prompt
    if (hasStoredLanguagePreference()) return null;
    const preferredSupportedLocale = getPreferredSupportedLocale(ADDITIONAL_SUPPORTED_LOCALES);
    // If the preferred supported locale is the same as the current language, don't show the prompt
    if (preferredSupportedLocale === i18n.language) return null;
    return preferredSupportedLocale;
  });

  const config = detectedLocale ? LOCALE_DETECTION_PROMPT_CONFIG[detectedLocale] : undefined;

  // If there is no detected locale with a config, don't open the modal
  const [isOpen, setIsOpen] = useState(!!config);

  const handleKeepEnglish = useCallback(() => {
    userClicked(EventTypeEnum.BROWSER_LANG_NOTICE_MODAL_KEEP, { suggested: detectedLocale! });
    setStoredLanguagePreference(LOCALE_DEFAULT);
    setIsOpen(false);
  }, [userClicked, detectedLocale]);

  const handleSwitchLanguage = useCallback(async () => {
    try {
      userClicked(EventTypeEnum.BROWSER_LANG_NOTICE_MODAL_SWITCH, {
        previous: i18n.language,
        current: detectedLocale!,
      });
      await i18n.changeLanguage(detectedLocale!);
      setStoredLanguagePreference(detectedLocale!);
    } catch (error) {
      logger.error(
        'Failed to change language from browser language prompt',
        { detectedLocale, previousLanguage: i18n.language },
        normalizeError(error),
      );
      return;
    }
    setIsOpen(false);
  }, [detectedLocale, i18n, userClicked]);

  useEffect(
    function fireBrowserLangModalViewedEvent() {
      if (isOpen && detectedLocale && !hasTrackedViewRef.current) {
        hasTrackedViewRef.current = true;
        userViewed(EventTypeEnum.BROWSER_LANG_NOTICE_MODAL_VIEWED, { suggested: detectedLocale });
      }
    },
    [isOpen, detectedLocale, userViewed],
  );

  if (!config || !isOpen) return null;

  return (
    <BrowserLanguagePromptModal
      isOpen={isOpen}
      config={config}
      onKeepEnglish={handleKeepEnglish}
      onSwitchLanguage={handleSwitchLanguage}
    />
  );
}

export function BrowserLanguagePromptWrapper() {
  const { value: isEnabled } = useFeatureGate(localizationTelehealthClientPromptGate);
  if (!isEnabled) return null;
  return <BrowserLanguagePrompt />;
}
